import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';

export type IfcAnnotation = {
  id: number;
  type: string;
  content: string | null;
  voice_key: string | null;
  ifc_global_id: string | null;
  user_name: string;
  created_at: string;
};

// Libellés FR pour les types IFC les plus courants. Les types non listés affichent leur nom IFC brut.
const CURATED_LABELS: Record<string, string> = {
  IFCWALL: 'Murs',
  IFCWALLSTANDARDCASE: 'Murs (standard)',
  IFCSLAB: 'Dalles',
  IFCSLABSTANDARDCASE: 'Dalles (standard)',
  IFCROOF: 'Toitures',
  IFCDOOR: 'Portes',
  IFCDOORSTANDARDCASE: 'Portes (standard)',
  IFCWINDOW: 'Fenêtres',
  IFCWINDOWSTANDARDCASE: 'Fenêtres (standard)',
  IFCCOLUMN: 'Colonnes',
  IFCCOLUMNSTANDARDCASE: 'Colonnes (standard)',
  IFCBEAM: 'Poutres',
  IFCBEAMSTANDARDCASE: 'Poutres (standard)',
  IFCSTAIR: 'Escaliers',
  IFCSTAIRFLIGHT: "Volées d'escalier",
  IFCRAMP: 'Rampes',
  IFCRAMPFLIGHT: 'Volées de rampe',
  IFCRAILING: 'Garde-corps',
  IFCCURTAINWALL: 'Murs rideaux',
  IFCSPACE: 'Espaces',
  IFCSITE: 'Site',
  IFCBUILDING: 'Bâtiment',
  IFCBUILDINGSTOREY: 'Étages',
  IFCCOVERING: 'Revêtements',
  IFCFURNISHINGELEMENT: 'Mobilier',
  IFCFURNITURE: 'Meubles',
  IFCPLATE: 'Plaques',
  IFCMEMBER: 'Membrures',
  IFCBUILDINGELEMENTPROXY: 'Éléments génériques',
  IFCOPENINGELEMENT: 'Ouvertures',
  IFCDUCTSEGMENT: 'Conduites aérauliques',
  IFCPIPESEGMENT: 'Tuyauteries',
  IFCCABLESEGMENT: 'Câblages',
  IFCFLOWSEGMENT: 'Segments de flux',
  IFCFLOWTERMINAL: 'Terminaux de flux',
  IFCAIRTERMINAL: 'Bouches air',
  IFCAIRTERMINALBOX: 'Boîtes air',
  IFCDISTRIBUTIONELEMENT: 'Élt. distribution',
  IFCDISTRIBUTIONPORT: 'Ports distribution',
  IFCSANITARYTERMINAL: 'Sanitaires',
  IFCLIGHTFIXTURE: 'Luminaires',
  IFCELECTRICAPPLIANCE: 'Appareils électriques',
  IFCSWITCHINGDEVICE: 'Appareillage',
  IFCOUTLET: 'Prises',
  IFCFASTENER: 'Fixations',
  IFCREINFORCINGBAR: "Armatures",
  IFCTRANSPORTELEMENT: 'Élt. transport',
  IFCSHADINGDEVICE: 'Protections solaires',
  IFCVIRTUALELEMENT: 'Éléments virtuels',
};

function labelForEntity(entity: string): string {
  return CURATED_LABELS[entity] ?? entity;
}

type PsetEntry = { name: string; props: Record<string, unknown> };

type ElementInfo = {
  expressID: number;
  ifcType: string;
  name: string | null;
  globalId: string | null;
  predefinedType: string | null;
  psets: PsetEntry[];
  storey: { expressID: number; name: string | null } | null;
  space: { expressID: number; name: string | null } | null;
  materials: string[];
};

interface Props {
  url: string;
  height?: string | number;
  documentId?: number;
  versionId?: number | null;
  annotations?: IfcAnnotation[];
  onAnnotationsChanged?: () => void;
  canEdit?: boolean;
}

function unwrap(v: any): any {
  if (v === null || v === undefined) return v;
  if (typeof v === 'object' && 'value' in v) return v.value;
  return v;
}

export default function IfcViewer({
  url,
  height = '70vh',
  documentId,
  versionId,
  annotations = [],
  onAnnotationsChanged,
  canEdit = true,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStep, setLoadingStep] = useState('Initialisation…');
  const [error, setError] = useState<string | null>(null);
  const [debugLines, setDebugLines] = useState<string[]>([]);
  const [showDebug, setShowDebug] = useState(false);

  const [selected, setSelected] = useState<ElementInfo | null>(null);
  const [sideOpen, setSideOpen] = useState(true);
  const [tab, setTab] = useState<'props' | 'annotations' | 'categories'>('props');
  const [hiddenEntities, setHiddenEntities] = useState<Set<string>>(new Set());
  const [entitiesInModel, setEntitiesInModel] = useState<Array<{ entity: string; count: number }>>(
    []
  );
  const [expandedEntity, setExpandedEntity] = useState<string | null>(null);
  const [hiddenElements, setHiddenElements] = useState<
    Array<{ expressID: number; fragmentID: string; name: string; ifcType: string }>
  >([]);
  const [ghostMode, setGhostMode] = useState(false);
  const [isolating, setIsolating] = useState(false);
  const [clipEnabled, setClipEnabled] = useState(false);
  const [clipAxes, setClipAxes] = useState<{ x: boolean; y: boolean; z: boolean }>({
    x: false,
    y: false,
    z: false,
  });
  const [clipBounds, setClipBounds] = useState<{
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  } | null>(null);
  const [clipValues, setClipValues] = useState<{ x: number; y: number; z: number }>({
    x: 0,
    y: 0,
    z: 0,
  });
  const [ready, setReady] = useState(false);
  const [annotationInfos, setAnnotationInfos] = useState<Record<number, ElementInfo>>({});

  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [recording, setRecording] = useState<{ recorder: MediaRecorder } | null>(null);

  // Capture 3D : menu + modes (still / turntable / annotated)
  const [captureMenuOpen, setCaptureMenuOpen] = useState(false);
  const [captureBusy, setCaptureBusy] = useState<string | null>(null);
  const [captureDraftUrl, setCaptureDraftUrl] = useState<string | null>(null);
  const [captureDraftBlob, setCaptureDraftBlob] = useState<Blob | null>(null);
  const [captureDraftMime, setCaptureDraftMime] = useState<string>('image/png');
  const [captureDraftKind, setCaptureDraftKind] = useState<'still' | 'turntable' | 'annotated'>(
    'still'
  );
  const [captureDraftMeta, setCaptureDraftMeta] = useState<{
    width: number;
    height: number;
    duration_ms?: number;
  } | null>(null);
  const [captureTitle, setCaptureTitle] = useState('');
  const [captureSaving, setCaptureSaving] = useState(false);
  // Annotations overlay pour le mode "annoté"
  const [annoItems, setAnnoItems] = useState<
    Array<{ id: string; x: number; y: number; tx: number; ty: number; label: string }>
  >([]);
  const [projectIdForCapture, setProjectIdForCapture] = useState<number | null>(null);

  const stateRef = useRef<any>(null);

  function log(msg: string) {
    const line = `${new Date().toISOString().slice(11, 19)} ${msg}`;
    // eslint-disable-next-line no-console
    console.log('[IFC]', msg);
    setDebugLines((d) => [...d, line]);
  }

  useEffect(() => {
    let cancelled = false;
    if (!documentId) return;
    (async () => {
      try {
        const r = await api.get<{ document: { project_id: number } }>(`/documents/${documentId}`);
        if (!cancelled) setProjectIdForCapture(r.document.project_id);
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // Rend la frame courante → dataURL (canvas.toDataURL est fiable après forçage d'un rendu)
  function renderFrameDataUrl(): string | null {
    const s = stateRef.current;
    if (!s?.world) return null;
    try {
      // Force un rendu synchrone
      const r = s.world.renderer?.three;
      const cam = s.world.camera?.three;
      const scene = s.world.scene?.three;
      if (r && cam && scene) r.render(scene, cam);
      const canvas: HTMLCanvasElement = s.world.renderer.three.domElement;
      return canvas.toDataURL('image/png');
    } catch (e) {
      log(`renderFrameDataUrl: ${String(e).slice(0, 120)}`);
      return null;
    }
  }

  async function captureStill() {
    setCaptureBusy('Capture…');
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const url = renderFrameDataUrl();
    if (!url) {
      setCaptureBusy(null);
      alert('Capture impossible.');
      return;
    }
    const blob = await (await fetch(url)).blob();
    const img = new Image();
    img.src = url;
    await new Promise((r) => {
      img.onload = r;
    });
    setCaptureDraftUrl(url);
    setCaptureDraftBlob(blob);
    setCaptureDraftMime('image/png');
    setCaptureDraftKind('still');
    setCaptureDraftMeta({ width: img.naturalWidth, height: img.naturalHeight });
    setCaptureTitle(`Capture ${new Date().toLocaleString('fr-CH')}`);
    setAnnoItems([]);
    setCaptureMenuOpen(false);
    setCaptureBusy(null);
  }

  async function captureAnnotated() {
    // Même base qu'un still, mais ouvre l'éditeur d'annotations
    setCaptureBusy('Capture pour annotation…');
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const url = renderFrameDataUrl();
    if (!url) {
      setCaptureBusy(null);
      alert('Capture impossible.');
      return;
    }
    const blob = await (await fetch(url)).blob();
    const img = new Image();
    img.src = url;
    await new Promise((r) => {
      img.onload = r;
    });
    setCaptureDraftUrl(url);
    setCaptureDraftBlob(blob);
    setCaptureDraftMime('image/png');
    setCaptureDraftKind('annotated');
    setCaptureDraftMeta({ width: img.naturalWidth, height: img.naturalHeight });
    setCaptureTitle(`Annotée ${new Date().toLocaleString('fr-CH')}`);
    setAnnoItems([]);
    setCaptureMenuOpen(false);
    setCaptureBusy(null);
  }

  async function captureTurntable() {
    const s = stateRef.current;
    if (!s?.world || !s.model) {
      alert('Modèle non prêt.');
      return;
    }
    setCaptureBusy('Turntable 0/36…');
    try {
      const THREE = s.THREE;
      const renderer = s.world.renderer.three;
      const camera = s.world.camera.three;
      const scene = s.world.scene.three;
      const canvas: HTMLCanvasElement = renderer.domElement;

      // Centre et distance de la caméra par rapport au modèle
      const bbox = new THREE.Box3().setFromObject(s.model);
      const center = bbox.getCenter(new THREE.Vector3());
      const size = bbox.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.z) * 1.2 + 1;

      const frames: string[] = [];
      const FRAMES = 36;
      const prevPos = camera.position.clone();
      const prevTarget = s.world.camera.controls?.getTarget?.();

      for (let i = 0; i < FRAMES; i++) {
        const angle = (i / FRAMES) * Math.PI * 2;
        const x = center.x + radius * Math.cos(angle);
        const z = center.z + radius * Math.sin(angle);
        const y = center.y + size.y * 0.35;
        if (s.world.camera.controls?.setLookAt) {
          await s.world.camera.controls.setLookAt(x, y, z, center.x, center.y, center.z, false);
        } else {
          camera.position.set(x, y, z);
          camera.lookAt(center);
        }
        renderer.render(scene, camera);
        frames.push(canvas.toDataURL('image/png'));
        setCaptureBusy(`Turntable ${i + 1}/${FRAMES}…`);
        await new Promise((r) => requestAnimationFrame(() => r(null)));
      }

      // Restaure la caméra
      if (prevTarget && s.world.camera.controls?.setLookAt) {
        await s.world.camera.controls.setLookAt(
          prevPos.x,
          prevPos.y,
          prevPos.z,
          prevTarget.x,
          prevTarget.y,
          prevTarget.z,
          false
        );
      } else {
        camera.position.copy(prevPos);
      }

      // Construit un WebM via un canvas hors écran + MediaRecorder
      setCaptureBusy('Encodage vidéo…');
      const firstImg = new Image();
      firstImg.src = frames[0];
      await new Promise((r) => {
        firstImg.onload = r;
      });
      const w = firstImg.naturalWidth;
      const h = firstImg.naturalHeight;
      const off = document.createElement('canvas');
      off.width = w;
      off.height = h;
      const octx = off.getContext('2d')!;
      const stream = (off as HTMLCanvasElement).captureStream(0);
      const track = stream.getVideoTracks()[0] as any;
      const chunks: Blob[] = [];
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      const done = new Promise<void>((res) => {
        rec.onstop = () => res();
      });
      rec.start();
      const fps = 18;
      for (let i = 0; i < frames.length * 2; i++) {
        const img = new Image();
        img.src = frames[i % frames.length];
        await new Promise((r) => {
          img.onload = r;
        });
        octx.clearRect(0, 0, w, h);
        octx.fillStyle = '#FAFAF5';
        octx.fillRect(0, 0, w, h);
        octx.drawImage(img, 0, 0, w, h);
        if (typeof track.requestFrame === 'function') track.requestFrame();
        await new Promise((r) => setTimeout(r, 1000 / fps));
      }
      rec.stop();
      await done;
      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      setCaptureDraftUrl(url);
      setCaptureDraftBlob(blob);
      setCaptureDraftMime(mime);
      setCaptureDraftKind('turntable');
      setCaptureDraftMeta({ width: w, height: h, duration_ms: Math.round((frames.length * 2 * 1000) / fps) });
      setCaptureTitle(`Turntable ${new Date().toLocaleString('fr-CH')}`);
      setAnnoItems([]);
      setCaptureMenuOpen(false);
    } catch (e) {
      log(`Turntable error: ${String(e).slice(0, 200)}`);
      alert('Turntable impossible.');
    } finally {
      setCaptureBusy(null);
    }
  }

  // Applique les annotations dessinées (flèches) sur le draft et rend un nouveau PNG
  async function flattenAnnotatedDraft(): Promise<Blob | null> {
    if (!captureDraftUrl || !captureDraftMeta) return null;
    const img = new Image();
    img.src = captureDraftUrl;
    await new Promise((r) => {
      img.onload = r;
    });
    const c = document.createElement('canvas');
    c.width = captureDraftMeta.width;
    c.height = captureDraftMeta.height;
    const ctx = c.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    ctx.lineWidth = Math.max(2, c.width / 400);
    ctx.strokeStyle = '#E07A5F';
    ctx.fillStyle = '#E07A5F';
    ctx.font = `${Math.max(14, c.width / 60)}px -apple-system, Segoe UI, sans-serif`;
    for (const a of annoItems) {
      const fromX = a.x * c.width;
      const fromY = a.y * c.height;
      const toX = a.tx * c.width;
      const toY = a.ty * c.height;
      // Ligne
      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(toX, toY);
      ctx.stroke();
      // Flèche
      const ang = Math.atan2(toY - fromY, toX - fromX);
      const size = Math.max(10, c.width / 80);
      ctx.beginPath();
      ctx.moveTo(toX, toY);
      ctx.lineTo(toX - size * Math.cos(ang - Math.PI / 6), toY - size * Math.sin(ang - Math.PI / 6));
      ctx.lineTo(toX - size * Math.cos(ang + Math.PI / 6), toY - size * Math.sin(ang + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
      // Label avec fond
      if (a.label) {
        const padding = 6;
        const metrics = ctx.measureText(a.label);
        const lw = metrics.width + padding * 2;
        const lh = Math.max(18, c.width / 50);
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.strokeStyle = '#E07A5F';
        ctx.fillRect(fromX + 6, fromY - lh - 4, lw, lh);
        ctx.strokeRect(fromX + 6, fromY - lh - 4, lw, lh);
        ctx.fillStyle = '#3D5A45';
        ctx.fillText(a.label, fromX + 6 + padding, fromY - 10);
        ctx.fillStyle = '#E07A5F';
      }
    }
    return new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/png', 0.95));
  }

  async function saveCaptureDraft() {
    if (!projectIdForCapture) {
      alert('Projet non déterminé.');
      return;
    }
    let blob = captureDraftBlob;
    let mime = captureDraftMime;
    let ext = mime.includes('webm') ? 'webm' : 'png';
    if (captureDraftKind === 'annotated') {
      const flat = await flattenAnnotatedDraft();
      if (flat) {
        blob = flat;
        mime = 'image/png';
        ext = 'png';
      }
    }
    if (!blob) return;
    setCaptureSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, `capture.${ext}`);
      fd.append('title', captureTitle || 'Capture 3D');
      fd.append('kind', captureDraftKind);
      if (documentId) fd.append('document_id', String(documentId));
      if (versionId) fd.append('version_id', String(versionId));
      if (captureDraftMeta) {
        fd.append('width', String(captureDraftMeta.width));
        fd.append('height', String(captureDraftMeta.height));
        if (captureDraftMeta.duration_ms)
          fd.append('duration_ms', String(captureDraftMeta.duration_ms));
      }
      const r = await fetch(`/api/v1/projects/${projectIdForCapture}/captures`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ error: 'Erreur' }));
        alert((e as any).error ?? 'Erreur');
        return;
      }
      // Feedback
      setCaptureDraftUrl(null);
      setCaptureDraftBlob(null);
      setCaptureDraftMeta(null);
      setAnnoItems([]);
      log('Capture sauvegardée.');
    } finally {
      setCaptureSaving(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => void) | null = null;

    (async () => {
      if (!hostRef.current) return;
      try {
        setLoadingStep('Chargement des bibliothèques…');
        log('Dynamic import OBC + THREE + WEBIFC');
        const OBC: any = await import('@thatopen/components');
        const THREE: any = await import('three');
        let WEBIFC: any = null;
        try {
          WEBIFC = await import('web-ifc');
          log(`web-ifc importé, ${Object.keys(WEBIFC).length} exports`);
        } catch (e) {
          log(`web-ifc indisponible: ${String(e).slice(0, 100)}`);
        }
        if (cancelled || !hostRef.current) return;

        log(`OBC exports (sample): ${Object.keys(OBC).slice(0, 15).join(', ')}`);

        const components = new OBC.Components();
        const worlds = components.get(OBC.Worlds);
        const world = worlds.create();
        world.scene = new OBC.SimpleScene(components);
        world.renderer = new OBC.SimpleRenderer(components, hostRef.current);
        world.camera = new OBC.SimpleCamera(components);
        components.init();
        world.scene.setup();
        world.scene.three.background = null;
        log('Components initialized, world created');

        setLoadingStep('Chargement du WASM IFC…');
        const ifcLoader = components.get(OBC.IfcLoader);
        await ifcLoader.setup({ autoSetWasm: true });
        log('IfcLoader.setup done');

        setLoadingStep('Téléchargement du modèle…');
        log(`Fetch ${url}`);
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status} au téléchargement`);
        const buf = new Uint8Array(await res.arrayBuffer());
        log(`Buffer reçu : ${buf.byteLength} octets`);

        setLoadingStep('Parsing IFC (peut prendre plusieurs secondes)…');
        log('ifcLoader.load() lancé…');
        const t0 = Date.now();
        const model: any = await ifcLoader.load(buf);
        log(`ifcLoader.load() terminé en ${Date.now() - t0} ms`);
        world.scene.three.add(model);
        try {
          world.camera.controls?.fitToSphere(model, true);
        } catch {
          /* noop */
        }

        // ----- Extraction de TOUTES les propriétés (synchrone) -----
        setLoadingStep('Extraction des propriétés IFC…');
        let localProps: Record<number, any> = {};
        try {
          const raw = typeof model.getLocalProperties === 'function' ? model.getLocalProperties() : null;
          if (raw) {
            // Convertit en Record<number, any> quelle que soit la forme (Map ou object)
            if (raw instanceof Map) {
              for (const [k, v] of raw.entries()) localProps[Number(k)] = v;
            } else {
              for (const [k, v] of Object.entries(raw)) localProps[Number(k)] = v;
            }
            log(`getLocalProperties: ${Object.keys(localProps).length} expressIDs`);
          } else {
            log('getLocalProperties renvoie null/undefined');
          }
        } catch (e) {
          log(`getLocalProperties a throw: ${String(e).slice(0, 150)}`);
        }

        // Dump des 3 premiers pour diagnostic
        const sample = Object.entries(localProps).slice(0, 3);
        for (const [id, p] of sample) {
          log(`  #${id}: type=${(p as any)?.type} Name=${unwrap((p as any)?.Name) ?? '∅'} GlobalId=${unwrap((p as any)?.GlobalId) ?? '∅'}`);
        }

        // Fallback: si aucune propriété, on tente getAllPropertiesOfType sur web-ifc directement
        if (Object.keys(localProps).length === 0 && WEBIFC && ifcLoader.webIfc) {
          log('Fallback web-ifc direct : réouverture du modèle');
          try {
            const apiDirect: any = ifcLoader.webIfc;
            const modelID = apiDirect.OpenModel(buf, {});
            log(`OpenModel direct → modelID=${modelID}`);
            stateRef.current = stateRef.current ?? {};
            stateRef.current.webIfcAPI = apiDirect;
            stateRef.current.webIfcModelID = modelID;
            // On remplit localProps à la demande pour chaque expressID via GetLine
            log('Mode fallback : propriétés lues à la demande');
          } catch (e) {
            log(`OpenModel direct a échoué: ${String(e).slice(0, 200)}`);
          }
        }

        // ----- Carte de types IFC (code numérique → "IFCWALL") -----
        const typeNameMap = new Map<number, string>();
        if (WEBIFC) {
          for (const [k, v] of Object.entries(WEBIFC)) {
            if (typeof v === 'number' && /^IFC[A-Z]/.test(k)) typeNameMap.set(v, k);
          }
        }
        log(`typeNameMap: ${typeNameMap.size} constantes IFC*`);

        // ----- Classification directe depuis localProps -----
        // Filtre : on ne garde que les IfcProduct (éléments physiques/spatiaux) —
        // détectés par la présence de ObjectPlacement. Ça exclut IfcRel*, IfcProperty*, etc.
        setLoadingStep('Classification des éléments…');
        const byEntity = new Map<string, number[]>();
        if (Object.keys(localProps).length > 0) {
          for (const [idStr, p] of Object.entries(localProps)) {
            const pp = p as any;
            if (!pp?.ObjectPlacement) continue; // skip non-products
            const typeCode = pp?.type;
            if (typeof typeCode !== 'number') continue;
            const name = typeNameMap.get(typeCode);
            if (!name) continue;
            if (!byEntity.has(name)) byEntity.set(name, []);
            byEntity.get(name)!.push(Number(idStr));
          }
        } else if (stateRef.current?.webIfcAPI) {
          // Fallback : GetLineIDsWithType pour chaque constante IFC connue
          const api = stateRef.current.webIfcAPI;
          const modelID = stateRef.current.webIfcModelID;
          for (const [entName, code] of Object.entries(WEBIFC as any)) {
            if (typeof code !== 'number' || !/^IFC[A-Z]/.test(entName)) continue;
            try {
              const vec = (api as any).GetLineIDsWithType(modelID, code);
              const ids: number[] = [];
              for (let i = 0; i < vec.size(); i++) ids.push(vec.get(i));
              if (ids.length > 0) byEntity.set(entName, ids);
            } catch {
              /* type non présent */
            }
          }
          log(`Fallback web-ifc classif : ${byEntity.size} entités avec éléments`);
        }

        log(`byEntity: ${byEntity.size} entités IFC distinctes`);
        const topEntities = [...byEntity.entries()]
          .sort((a, b) => b[1].length - a[1].length)
          .slice(0, 8)
          .map(([k, v]) => `${k}=${v.length}`)
          .join(', ');
        log(`Top entités: ${topEntities}`);

        // Liste triée des entités présentes dans le modèle (toutes, dynamiquement)
        const entitiesList = [...byEntity.entries()]
          .map(([entity, ids]) => ({ entity, count: ids.length }))
          .sort((a, b) => b.count - a.count);

        // ----- Classifier @thatopen pour opérations visuelles -----
        const classifier = components.get(OBC.Classifier);
        const hider = components.get(OBC.Hider);
        try {
          classifier.byEntity(model);
          const entKeys = Object.keys(classifier.list?.entities ?? {});
          log(`Classifier.byEntity → entities: ${entKeys.slice(0, 8).join(', ')}${entKeys.length > 8 ? '…' : ''}`);
        } catch (e) {
          log(`classifier.byEntity a échoué: ${String(e).slice(0, 150)}`);
        }


        // ----- Raycaster : on garde @thatopen en fallback mais on pilote nativement THREE -----
        const casters = components.get(OBC.Raycasters);
        const caster = casters.get(world);
        const rayCaster = new THREE.Raycaster();
        rayCaster.firstHitOnly = false;
        const pointer = new THREE.Vector2();
        log('Raycaster prêt (THREE natif + fallback OBC)');

        // ----- Fragments manager -----
        const fragments = components.get(OBC.FragmentsManager);
        log(`fragments.meshes: ${fragments.meshes?.length ?? 0}`);

        // ----- Clip planes THREE direct : 3 axes indépendants -----
        const bbox = new THREE.Box3().setFromObject(model);
        const safe = (v: number, fb: number) => (isFinite(v) ? v : fb);
        const minX = safe(bbox.min.x, -20);
        const maxX = safe(bbox.max.x, 20);
        const minY = safe(bbox.min.y, -5);
        const maxY = safe(bbox.max.y, 20);
        const minZ = safe(bbox.min.z, -20);
        const maxZ = safe(bbox.max.z, 20);
        log(
          `Bounds : X[${minX.toFixed(2)},${maxX.toFixed(2)}] Y[${minY.toFixed(2)},${maxY.toFixed(2)}] Z[${minZ.toFixed(2)},${maxZ.toFixed(2)}]`
        );
        // Normales négatives → constant = max maintient tout, constant = min masque tout
        const clipPlaneX = new THREE.Plane(new THREE.Vector3(-1, 0, 0), maxX);
        const clipPlaneY = new THREE.Plane(new THREE.Vector3(0, -1, 0), maxY);
        const clipPlaneZ = new THREE.Plane(new THREE.Vector3(0, 0, -1), maxZ);
        const rendererThree = world.renderer.three;
        rendererThree.localClippingEnabled = false;
        rendererThree.clippingPlanes = [];
        setClipBounds({
          min: { x: minX, y: minY, z: minZ },
          max: { x: maxX, y: maxY, z: maxZ },
        });
        setClipValues({ x: maxX, y: maxY, z: maxZ });

        // GlobalId → expressID (pour lier les annotations aux meshes)
        const globalIdToExpressID = new Map<string, number>();
        for (const [idStr, p] of Object.entries(localProps)) {
          const gid = unwrap((p as any)?.GlobalId);
          if (typeof gid === 'string' && gid.length > 0) {
            globalIdToExpressID.set(gid, Number(idStr));
          }
        }
        log(`globalIdToExpressID : ${globalIdToExpressID.size} mappings`);

        // ----- Interactions souris -----
        const canvas: HTMLCanvasElement = world.renderer.three.domElement;
        let downPos: { x: number; y: number } | null = null;
        let selectedMap: any = null;
        const highlightColor = new THREE.Color('#ff7043');
        const annotatedColor = new THREE.Color('#3C8DBC');

        const onDown = (e: PointerEvent) => {
          downPos = { x: e.clientX, y: e.clientY };
        };

        const onUp = async (e: PointerEvent) => {
          if (!downPos) return;
          const dx = e.clientX - downPos.x;
          const dy = e.clientY - downPos.y;
          downPos = null;
          if (dx * dx + dy * dy > 25) return; // drag

          try {
            // --- Raycast natif THREE : plus robuste que OBC.Raycasters pour meshes transparents/fins ---
            const rect = canvas.getBoundingClientRect();
            pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
            rayCaster.setFromCamera(pointer, world.camera.three);

            const allMeshes = (fragments.meshes ?? []).filter((m: any) => m?.visible);
            let nativeHits = rayCaster.intersectObjects(allMeshes, false);

            // En fallback : inclure tous les meshes (même invisibles/autres objets) via traverse
            if (nativeHits.length === 0) {
              const broadMeshes: any[] = [];
              model.traverse((o: any) => {
                if (o?.isMesh && o.visible) broadMeshes.push(o);
              });
              nativeHits = rayCaster.intersectObjects(broadMeshes, false);
              log(
                `Raycast natif vide sur fragments.meshes (${allMeshes.length}), traverse→${broadMeshes.length} meshes, hits=${nativeHits.length}`
              );
            }

            // Filtre : premier hit avec fragment + instanceId valide + expressID valide
            let chosen: { fragment: any; instanceId: number; expressID: number } | null = null;
            for (const h of nativeHits) {
              const fragment: any = (h.object as any)?.fragment;
              if (!fragment) continue;
              const instanceId: number = h.instanceId ?? 0;
              let eID: number | null = null;
              if (typeof fragment.getItemID === 'function') {
                eID = fragment.getItemID(instanceId);
              }
              if (eID !== null && eID !== undefined) {
                chosen = { fragment, instanceId, expressID: eID };
                break;
              }
            }

            // Dernier recours : OBC caster
            if (!chosen) {
              const obcHit = caster.castRay(allMeshes);
              if (obcHit) {
                const fragment: any = (obcHit as any).object?.fragment;
                const instanceId: number = (obcHit as any).instanceId ?? 0;
                if (fragment && typeof fragment.getItemID === 'function') {
                  const eID = fragment.getItemID(instanceId);
                  if (eID !== null && eID !== undefined) {
                    chosen = { fragment, instanceId, expressID: eID };
                    log('Hit via OBC caster (fallback)');
                  }
                }
              }
            }

            if (!chosen) {
              log(
                `Clic : aucun hit (hits natifs=${nativeHits.length}, meshes visibles=${allMeshes.length})`
              );
              selectedMap = null;
              // Clic dans le vide : désactive le ghost et tout redevient opaque
              applyGhost(false);
              setGhostMode(false);
              repaintAll();
              setSelected(null);
              return;
            }

            const { fragment, instanceId, expressID } = chosen;
            log(`Clic: fragment=${fragment.id} instance=${instanceId} → expressID=${expressID}`);

            selectedMap = { [fragment.id]: new Set([expressID]) };
            // Translucidité auto : tous les autres éléments à 15 %, l'élément sélectionné reste opaque
            applyGhost(true);
            setGhostMode(true);
            repaintAll();

            // Lit propriétés depuis localProps OU via GetLine direct
            let props: any = localProps[expressID];
            if (!props && stateRef.current?.webIfcAPI) {
              try {
                props = stateRef.current.webIfcAPI.GetLine(
                  stateRef.current.webIfcModelID,
                  expressID,
                  false,
                  false
                );
                if (props) localProps[expressID] = props;
              } catch (e) {
                log(`GetLine fallback échoué pour ${expressID}: ${String(e).slice(0, 100)}`);
              }
            }

            const info = buildElementInfo(expressID, props, localProps, typeNameMap, WEBIFC);
            log(`Info: type=${info.ifcType} name=${info.name} psets=${info.psets.length}`);
            setSelected(info);
            setSideOpen(true);
            setTab('props');
          } catch (e) {
            log(`Handler click a throw: ${String(e).slice(0, 200)}`);
          }
        };

        // Repaint : applique bleu sur annotated minus selected, orange sur selected
        function repaintAll() {
          const annotatedMap = stateRef.current?.annotatedMap ?? null;
          const lastPainted = stateRef.current?.lastPainted ?? null;
          if (lastPainted) {
            try {
              classifier.resetColor(lastPainted);
            } catch {
              /* noop */
            }
          }
          // Build annotatedMap sans les ids du selectedMap
          const paintAnnot: Record<string, Set<number>> = {};
          if (annotatedMap) {
            for (const fragId of Object.keys(annotatedMap)) {
              const src = annotatedMap[fragId] as Set<number>;
              const copy = new Set<number>();
              for (const id of src) {
                if (selectedMap?.[fragId]?.has(id)) continue;
                copy.add(id);
              }
              if (copy.size > 0) paintAnnot[fragId] = copy;
            }
          }
          try {
            if (Object.keys(paintAnnot).length > 0) {
              classifier.setColor(paintAnnot, annotatedColor);
            }
          } catch (e) {
            log(`setColor annot échec: ${String(e).slice(0, 100)}`);
          }
          try {
            if (selectedMap && Object.keys(selectedMap).length > 0) {
              classifier.setColor(selectedMap, highlightColor);
            }
          } catch (e) {
            log(`setColor select échec: ${String(e).slice(0, 100)}`);
          }
          const combined: Record<string, Set<number>> = { ...paintAnnot };
          if (selectedMap) {
            for (const k of Object.keys(selectedMap)) {
              const cur = combined[k] ? new Set(combined[k]) : new Set<number>();
              for (const id of selectedMap[k]) cur.add(id);
              combined[k] = cur;
            }
          }
          stateRef.current.lastPainted = combined;
        }

        // Helper : trouver le fragmentID contenant un expressID
        function findFragmentForExpressID(expressID: number): string | null {
          try {
            const list: any = fragments.list;
            if (!list) return null;
            const iter =
              list.entries?.() ??
              (() => {
                const arr: Array<[any, any]> = [];
                list.forEach?.((v: any, k: any) => arr.push([k, v]));
                return arr[Symbol.iterator]();
              })();
            for (const [fragId, frag] of iter as Iterable<[any, any]>) {
              const items = (frag as any)?.ids ?? (frag as any)?.items;
              if (!items) continue;
              const has =
                items instanceof Set
                  ? items.has(expressID)
                  : items instanceof Map
                    ? items.has(expressID)
                    : Array.isArray(items)
                      ? items.includes(expressID)
                      : false;
              if (has) return String(fragId);
            }
          } catch {
            /* noop */
          }
          return null;
        }

        // Helper : zoom caméra sur un élément précis via sa bbox d'instance
        function flyToExpressID(fragmentID: string, expressID: number) {
          try {
            const fragment: any = fragments.list?.get?.(fragmentID);
            if (!fragment) return;
            const mesh = fragment.mesh;
            if (!mesh) return;

            // Chercher l'instanceID correspondant à l'expressID
            let instanceID: number | null = null;
            try {
              if (typeof fragment.getInstancesIDs === 'function') {
                const s = fragment.getInstancesIDs(new Set([expressID]));
                instanceID = s?.values?.().next()?.value ?? null;
              }
            } catch {
              /* noop */
            }
            if (instanceID === null || instanceID === undefined) {
              if (fragment.ids instanceof Map) {
                const v = fragment.ids.get(expressID);
                if (v instanceof Set) instanceID = v.values().next().value ?? null;
                else if (typeof v === 'number') instanceID = v;
              }
            }

            // Geometry bbox (local)
            const geom = mesh.geometry;
            if (!geom.boundingBox) geom.computeBoundingBox();
            if (!geom.boundingBox) return;

            let worldBbox: any;
            if (instanceID !== null && instanceID !== undefined) {
              const m = new THREE.Matrix4();
              mesh.getMatrixAt(instanceID, m);
              worldBbox = geom.boundingBox.clone().applyMatrix4(m);
            } else {
              // Fallback : bbox du mesh complet
              worldBbox = new THREE.Box3().setFromObject(mesh);
            }

            const controls: any = world.camera?.controls;
            if (controls?.fitToBox) {
              controls.fitToBox(worldBbox, true, {
                paddingLeft: 1,
                paddingRight: 1,
                paddingTop: 1,
                paddingBottom: 1,
              });
              log(`flyTo fragment=${fragmentID} instance=${instanceID} OK`);
            } else if (controls?.fitToSphere) {
              const sphere = worldBbox.getBoundingSphere(new THREE.Sphere());
              controls.fitToSphere(sphere, true);
              log(`flyTo (fitToSphere fallback) OK`);
            } else {
              log('Pas de méthode fit sur controls');
            }
          } catch (e) {
            log(`flyToExpressID throw: ${String(e).slice(0, 150)}`);
          }
        }

        // Helper : sélectionner un élément programmatiquement (depuis la liste annotations)
        async function selectByExpressID(expressID: number, opts?: { ghost?: boolean }) {
          let fragId = findFragmentForExpressID(expressID);
          if (!fragId) {
            log(`selectByExpressID: pas de fragment pour ${expressID}`);
            return;
          }
          selectedMap = { [fragId]: new Set([expressID]) };
          repaintAll();
          const props = localProps[expressID];
          const info = buildElementInfo(expressID, props, localProps, typeNameMap, WEBIFC);
          setSelected(info);
          setSideOpen(true);
          setTab('props');
          flyToExpressID(fragId, expressID);
          if (opts?.ghost !== false) {
            applyGhost(true);
            setGhostMode(true);
          }
        }

        // Ghost mode — approche scene.traverse + clone+Map backup+dispose.
        // Map : mesh → matériau original (ou tableau de matériaux). Sert à restaurer.
        const originalMaterials = new Map<any, any>();

        function makeGhost(m: any) {
          const c = typeof m?.clone === 'function' ? m.clone() : m;
          c.transparent = true;
          c.opacity = 0.1;
          c.depthWrite = false;
          c.needsUpdate = true;
          return c;
        }

        function disposeCurrent(mesh: any) {
          const cur = mesh.material;
          if (!cur) return;
          if (Array.isArray(cur)) {
            for (const m of cur) m?.dispose?.();
          } else {
            cur.dispose?.();
          }
        }

        function applyGhost(enabled: boolean) {
          const sceneThree: any = world.scene.three;
          const sel = selectedMap;
          const protectedIds = new Set<string>();
          if (sel) for (const k of Object.keys(sel)) protectedIds.add(String(k));

          if (!enabled) {
            // Restauration : on remet partout le matériau d'origine et on dispose le clone
            let restored = 0;
            for (const [mesh, origMat] of originalMaterials.entries()) {
              disposeCurrent(mesh);
              mesh.material = origMat;
              restored++;
            }
            originalMaterials.clear();
            log(`Ghost OFF : ${restored} matériaux restaurés`);
            return;
          }

          // Ghost ON
          // 1) Si un mesh déjà ghosté appartient désormais à la sélection, on le restaure
          let unghosted = 0;
          for (const [mesh, origMat] of [...originalMaterials.entries()]) {
            const fid = mesh.fragment?.id;
            if (fid !== undefined && protectedIds.has(String(fid))) {
              disposeCurrent(mesh);
              mesh.material = origMat;
              originalMaterials.delete(mesh);
              unghosted++;
            }
          }

          // 2) Ghost tout mesh non-protégé pas encore ghosté — via scene.traverse (couvre TOUT)
          let ghosted = 0;
          let protectedCount = 0;
          let skippedAlreadyGhosted = 0;
          sceneThree.traverse((obj: any) => {
            if (!obj?.isMesh) return;
            const fid = obj.fragment?.id;
            if (fid !== undefined && protectedIds.has(String(fid))) {
              protectedCount++;
              return;
            }
            if (originalMaterials.has(obj)) {
              skippedAlreadyGhosted++;
              return;
            }
            const origMat = obj.material;
            if (!origMat) return;
            originalMaterials.set(obj, origMat);
            obj.material = Array.isArray(origMat)
              ? origMat.map(makeGhost)
              : makeGhost(origMat);
            ghosted++;
          });
          log(
            `Ghost ON : +${ghosted} ghostés, -${unghosted} restaurés (protégés), ${protectedCount} protégés trouvés, ${skippedAlreadyGhosted} déjà ghostés. Total mémoire : ${originalMaterials.size}`
          );
        }

        // Isole l'élément sélectionné (cache tout le reste via Hider).
        function isolateSelected() {
          const sel = selectedMap;
          if (!sel) {
            alert("Sélectionne d'abord un élément à isoler.");
            return;
          }
          const targetFragId = Object.keys(sel)[0];
          const targetID = [...(sel[targetFragId] as Set<number>)][0];
          if (!targetFragId || targetID === undefined) return;

          // Construit un map "tout sauf la cible"
          const toHide: Record<string, Set<number>> = {};
          const list: any = fragments.list;
          if (!list) return;
          try {
            const iter =
              list.entries?.() ??
              (() => {
                const arr: Array<[any, any]> = [];
                list.forEach?.((v: any, k: any) => arr.push([k, v]));
                return arr[Symbol.iterator]();
              })();
            for (const [fragId, frag] of iter as Iterable<[any, any]>) {
              const items = (frag as any)?.ids ?? (frag as any)?.items;
              if (!items) continue;
              const allIds: number[] =
                items instanceof Set
                  ? [...items]
                  : items instanceof Map
                    ? [...items.keys()]
                    : Array.isArray(items)
                      ? items
                      : [];
              const set = new Set<number>();
              for (const id of allIds) {
                if (String(fragId) === targetFragId && id === targetID) continue;
                set.add(id);
              }
              if (set.size > 0) toHide[String(fragId)] = set;
            }
            hider.set(false, toHide);
            stateRef.current.isolationMap = toHide;
            setIsolating(true);
            // Désactive le ghost (l'isolation remplace le fade)
            applyGhost(false);
            setGhostMode(false);
            log(
              `Isolate OK : ${Object.values(toHide).reduce((n, s) => n + (s as Set<number>).size, 0)} éléments cachés`
            );
          } catch (e) {
            log(`Isolate a throw: ${String(e).slice(0, 150)}`);
          }
        }

        function unisolate() {
          if (!stateRef.current?.isolationMap) {
            setIsolating(false);
            return;
          }
          try {
            hider.set(true, stateRef.current.isolationMap);
          } catch (e) {
            log(`Unisolate a throw: ${String(e).slice(0, 150)}`);
          }
          stateRef.current.isolationMap = null;
          setIsolating(false);
          // Ré-applique les catégories / éléments individuels qui étaient masqués avant l'isolation
          // (restaurés par l'appel set(true, …) ci-dessus, donc on ré-hide ce qui doit l'être)
        }

        canvas.addEventListener('pointerdown', onDown);
        canvas.addEventListener('pointerup', onUp);

        stateRef.current = {
          ...(stateRef.current ?? {}),
          components,
          world,
          model,
          fragments,
          classifier,
          hider,
          localProps,
          byEntity,
          typeNameMap,
          globalIdToExpressID,
          clipPlaneX,
          clipPlaneY,
          clipPlaneZ,
          annotatedMap: null as any,
          lastPainted: null as any,
          WEBIFC,
          THREE,
          highlightColor,
          annotatedColor,
          repaintAll,
          selectByExpressID,
          findFragmentForExpressID,
          flyToExpressID,
          applyGhost,
          isolateSelected,
          unisolate,
          ghostMode: false,
          isolationMap: null,
          getSelectedMap: () => selectedMap,
          setSelectedMap: (m: any) => {
            selectedMap = m;
          },
        };

        setEntitiesInModel(entitiesList);
        setLoading(false);
        setReady(true);
        log('Viewer prêt');

        cleanup = () => {
          try {
            canvas.removeEventListener('pointerdown', onDown);
            canvas.removeEventListener('pointerup', onUp);
            components.dispose();
          } catch {
            /* noop */
          }
        };
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        log(`FATAL: ${msg}`);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [url]);

  // Construit le FragmentIdMap (fragId → Set<expressID>) pour un ensemble d'expressIDs
  function buildFragMap(expressIDs: Iterable<number>): Record<string, Set<number>> {
    if (!stateRef.current) return {};
    const { fragments } = stateRef.current;
    const idsSet = new Set<number>(expressIDs);
    if (idsSet.size === 0) return {};
    const out: Record<string, Set<number>> = {};
    const list: any = fragments.list;
    if (!list) return out;
    try {
      const iter =
        list.entries?.() ??
        (() => {
          const arr: Array<[any, any]> = [];
          list.forEach?.((v: any, k: any) => arr.push([k, v]));
          return arr[Symbol.iterator]();
        })();
      for (const [fragId, frag] of iter as Iterable<[any, any]>) {
        const items = (frag as any)?.ids ?? (frag as any)?.items;
        if (!items) continue;
        const matched = new Set<number>();
        if (items instanceof Set || items instanceof Map) {
          for (const id of idsSet) if (items.has(id)) matched.add(id);
        } else if (Array.isArray(items)) {
          for (const id of idsSet) if (items.includes(id)) matched.add(id);
        }
        if (matched.size > 0) out[String(fragId)] = matched;
      }
    } catch {
      /* noop */
    }
    return out;
  }

  function toggleEntity(entity: string) {
    if (!stateRef.current) return;
    const { hider, byEntity, classifier } = stateRef.current;
    const isHidden = hiddenEntities.has(entity);
    const next = new Set(hiddenEntities);
    isHidden ? next.delete(entity) : next.add(entity);
    setHiddenEntities(next);

    // Tentative : classifier.find (rapide si ça marche)
    let map: any = null;
    try {
      map = classifier.find({ entities: [entity] });
    } catch {
      /* noop */
    }
    const count = map ? Object.keys(map).reduce((n, k) => n + (map[k]?.size ?? 0), 0) : 0;
    if (count === 0) {
      // Fallback : scan manuel des fragments
      const ids = byEntity.get(entity) ?? [];
      map = buildFragMap(ids);
      log(`toggleEntity ${entity}: map manuelle (${Object.keys(map).length} frags)`);
    }

    try {
      hider.set(isHidden, map);
    } catch (e) {
      log(`hider.set a throw: ${String(e).slice(0, 150)}`);
    }
    stateRef.current?.repaintAll?.();
  }

  function hideSelected() {
    if (!selected || !stateRef.current) return;
    const expressID = selected.expressID;
    const { hider, findFragmentForExpressID } = stateRef.current;
    const fragId = findFragmentForExpressID(expressID);
    if (!fragId) {
      alert("Impossible de localiser cet élément dans les fragments.");
      return;
    }
    const map = { [fragId]: new Set([expressID]) };
    try {
      hider.set(false, map);
    } catch (e) {
      log(`hider.set (hideSelected) a throw: ${String(e).slice(0, 100)}`);
      return;
    }
    setHiddenElements((arr) => [
      ...arr,
      {
        expressID,
        fragmentID: fragId,
        name: selected.name ?? '(sans nom)',
        ifcType: selected.ifcType,
      },
    ]);
    // Désélectionne (la couleur orange disparaît avec l'élément masqué)
    stateRef.current.setSelectedMap?.(null);
    setSelected(null);
    stateRef.current.repaintAll?.();
  }

  function showHiddenElement(h: { expressID: number; fragmentID: string }) {
    if (!stateRef.current) return;
    const { hider } = stateRef.current;
    const map = { [h.fragmentID]: new Set([h.expressID]) };
    try {
      hider.set(true, map);
    } catch {
      /* noop */
    }
    setHiddenElements((arr) => arr.filter((x) => x.expressID !== h.expressID));
    stateRef.current.repaintAll?.();
  }

  function showAllHidden() {
    if (!stateRef.current || hiddenElements.length === 0) return;
    const { hider } = stateRef.current;
    const union: Record<string, Set<number>> = {};
    for (const h of hiddenElements) {
      if (!union[h.fragmentID]) union[h.fragmentID] = new Set();
      union[h.fragmentID].add(h.expressID);
    }
    try {
      hider.set(true, union);
    } catch {
      /* noop */
    }
    setHiddenElements([]);
    stateRef.current.repaintAll?.();
  }

  // Synchronise le state React ghostMode avec stateRef (utilisé dans le click handler)
  useEffect(() => {
    if (stateRef.current) stateRef.current.ghostMode = ghostMode;
  }, [ghostMode]);

  // Enrichit chaque annotation avec les infos de son élément IFC (nom, type, étage, matériaux)
  useEffect(() => {
    if (!ready || !stateRef.current) return;
    const { localProps, globalIdToExpressID, typeNameMap, WEBIFC } = stateRef.current;
    if (!localProps || !globalIdToExpressID) return;
    const out: Record<number, ElementInfo> = {};
    for (const a of annotations) {
      if (!a.ifc_global_id) continue;
      const expressID = globalIdToExpressID.get(a.ifc_global_id);
      if (typeof expressID !== 'number') continue;
      const props = localProps[expressID];
      if (!props) continue;
      try {
        out[a.id] = buildElementInfo(expressID, props, localProps, typeNameMap, WEBIFC);
      } catch {
        /* noop */
      }
    }
    setAnnotationInfos(out);
  }, [annotations, ready]);

  // Clic sur une annotation dans la liste → sélectionne l'élément + zoom caméra
  function onAnnotationListClick(globalId: string | null) {
    if (!globalId || !stateRef.current) return;
    const expressID = stateRef.current.globalIdToExpressID?.get(globalId);
    if (typeof expressID !== 'number') {
      log(`Annotation clic : GlobalId ${globalId} introuvable dans le modèle`);
      return;
    }
    stateRef.current.selectByExpressID?.(expressID);
  }

  async function deleteAnnotation(annotationId: number) {
    if (!confirm('Supprimer cette annotation ?')) return;
    try {
      await api.del(`/annotations/${annotationId}`);
      onAnnotationsChanged?.();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Suppression impossible');
    }
  }

  // Rebuild annotatedMap (fragmentID → Set<expressID>) et repaint quand annotations changent
  useEffect(() => {
    if (!ready || !stateRef.current) return;
    const { fragments, globalIdToExpressID, repaintAll } = stateRef.current;
    if (!fragments || !globalIdToExpressID || !repaintAll) return;

    // 1. Annotations valides qui ont un GlobalId présent dans le modèle
    const targetIds = new Set<number>();
    for (const a of annotations) {
      if (!a.ifc_global_id) continue;
      const id = globalIdToExpressID.get(a.ifc_global_id);
      if (typeof id === 'number') targetIds.add(id);
    }

    // 2. Map fragmentID → Set<expressID> en scannant les fragments
    const annotatedMap: Record<string, Set<number>> = {};
    try {
      const list: any = fragments.list;
      const iter =
        list?.entries?.() ??
        (list?.forEach
          ? (() => {
              const arr: Array<[any, any]> = [];
              list.forEach((v: any, k: any) => arr.push([k, v]));
              return arr[Symbol.iterator]();
            })()
          : []);
      for (const [fragId, frag] of iter as Iterable<[any, any]>) {
        const fragItems = (frag as any)?.ids ?? (frag as any)?.items;
        if (!fragItems) continue;
        const matched = new Set<number>();
        for (const id of targetIds) {
          const has =
            fragItems instanceof Set
              ? fragItems.has(id)
              : Array.isArray(fragItems)
                ? fragItems.includes(id)
                : false;
          if (has) matched.add(id);
        }
        if (matched.size > 0) annotatedMap[String(fragId)] = matched;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[IFC] build annotatedMap failed', e);
    }

    stateRef.current.annotatedMap = annotatedMap;
    repaintAll();
  }, [annotations, ready]);

  // Clip global : active/désactive le mode coupe. Les axes (X/Y/Z) sont togglés individuellement.
  function toggleClip() {
    if (!stateRef.current) return;
    const renderer = stateRef.current.world?.renderer?.three;
    if (!renderer) return;
    if (clipEnabled) {
      renderer.localClippingEnabled = false;
      renderer.clippingPlanes = [];
      setClipEnabled(false);
      setClipAxes({ x: false, y: false, z: false });
      log('Clip OFF');
    } else {
      renderer.clippingPlanes = [];
      renderer.localClippingEnabled = true;
      setClipEnabled(true);
      log('Clip ON (active X / Y / Z individuellement)');
    }
  }

  function applyActivePlanes(nextAxes: { x: boolean; y: boolean; z: boolean }) {
    if (!stateRef.current) return;
    const renderer = stateRef.current.world?.renderer?.three;
    if (!renderer) return;
    const planes: any[] = [];
    if (nextAxes.x) planes.push(stateRef.current.clipPlaneX);
    if (nextAxes.y) planes.push(stateRef.current.clipPlaneY);
    if (nextAxes.z) planes.push(stateRef.current.clipPlaneZ);
    renderer.clippingPlanes = planes;
  }

  function toggleAxis(axis: 'x' | 'y' | 'z') {
    const next = { ...clipAxes, [axis]: !clipAxes[axis] };
    setClipAxes(next);
    applyActivePlanes(next);
    log(`Axe ${axis.toUpperCase()} = ${next[axis] ? 'ON' : 'OFF'}`);
  }

  function moveClip(axis: 'x' | 'y' | 'z', v: number) {
    if (!stateRef.current) return;
    const plane =
      axis === 'x'
        ? stateRef.current.clipPlaneX
        : axis === 'y'
          ? stateRef.current.clipPlaneY
          : stateRef.current.clipPlaneZ;
    if (!plane) return;
    plane.constant = v;
    setClipValues((c) => ({ ...c, [axis]: v }));
  }

  async function addTextAnnotation() {
    if (!documentId || !selected?.globalId || !commentText.trim()) {
      setCommentOpen(false);
      setCommentText('');
      return;
    }
    await api.post(`/documents/${documentId}/annotations`, {
      type: '3d',
      content: commentText.trim(),
      ifc_global_id: selected.globalId,
      version_id: versionId ?? null,
    });
    setCommentText('');
    setCommentOpen(false);
    onAnnotationsChanged?.();
  }

  async function startRecording() {
    if (!selected?.globalId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunks.push(ev.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        if (!documentId || !selected?.globalId) {
          setRecording(null);
          return;
        }
        const fd = new FormData();
        fd.append('audio', blob, 'memo.webm');
        fd.append('ifc_global_id', selected.globalId);
        if (versionId) fd.append('version_id', String(versionId));
        const res = await fetch(`/api/v1/documents/${documentId}/voice-memo`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!res.ok) alert("Impossible d'enregistrer le mémo vocal.");
        else onAnnotationsChanged?.();
        setRecording(null);
      };
      rec.start();
      setRecording({ recorder: rec });
    } catch (e) {
      alert("Accès micro refusé.");
    }
  }

  function stopRecording() {
    recording?.recorder.stop();
  }

  const annsForSelected = selected?.globalId
    ? annotations.filter((a) => a.ifc_global_id === selected.globalId)
    : [];
  const allIfcAnnotations = annotations.filter((a) => a.ifc_global_id);

  return (
    <div className="ifc-viewer" style={{ position: 'relative', width: '100%', height }}>
      <div
        ref={hostRef}
        className="ifc-canvas"
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid var(--c-border)',
          borderRadius: 10,
          overflow: 'hidden',
          background: '#eef1ea',
        }}
      />

      {loading && !error && (
        <div className="ifc-overlay">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, marginBottom: 6 }}>{loadingStep}</div>
            <div className="card-meta" style={{ fontSize: 11 }}>
              (Ouvre 🐛 pour voir les détails de chargement)
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="ifc-overlay ifc-error">
          <div style={{ maxWidth: 540, padding: 20, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Erreur IFC</div>
            <div style={{ fontSize: 13, marginBottom: 10 }}>{error}</div>
            <button className="btn btn-ghost" onClick={() => setShowDebug(true)}>
              Voir le log (🐛)
            </button>
          </div>
        </div>
      )}

      <div className="ifc-toolbar">
        <button
          className={`pdf-mode-btn ${sideOpen ? 'active' : ''}`}
          onClick={() => setSideOpen((v) => !v)}
          title="Panneau latéral"
        >
          ☰
        </button>
        <button
          className={`pdf-mode-btn ${clipEnabled ? 'active' : ''}`}
          onClick={toggleClip}
          title="Active le mode coupe avec sliders X / Y / Z"
        >
          ✂ {clipEnabled ? 'Coupe ON' : 'Coupe'}
        </button>
        <button
          className="pdf-mode-btn"
          onClick={() => {
            setTab('categories');
            setSideOpen(true);
          }}
        >
          👁 Catégories
        </button>
        <div className="ifc-capture-wrap" style={{ position: 'relative' }}>
          <button
            className={`pdf-mode-btn ${captureMenuOpen ? 'active' : ''}`}
            onClick={() => setCaptureMenuOpen((v) => !v)}
            title="Capturer la vue 3D"
            disabled={!!captureBusy || !ready}
          >
            📸 {captureBusy ?? 'Capturer'}
          </button>
          {captureMenuOpen && (
            <div className="ifc-capture-menu">
              <button
                className="ifc-capture-item"
                onClick={captureStill}
                disabled={!!captureBusy}
              >
                <span className="ic">📷</span>
                <div>
                  <div className="ttl">Vue simple</div>
                  <div className="sub">Capture du canvas tel qu'affiché</div>
                </div>
              </button>
              <button
                className="ifc-capture-item"
                onClick={captureTurntable}
                disabled={!!captureBusy}
              >
                <span className="ic">🎞</span>
                <div>
                  <div className="ttl">Turntable 360°</div>
                  <div className="sub">Animation vidéo du modèle</div>
                </div>
              </button>
              <button
                className="ifc-capture-item"
                onClick={captureAnnotated}
                disabled={!!captureBusy}
              >
                <span className="ic">🏷</span>
                <div>
                  <div className="ttl">Capture annotée</div>
                  <div className="sub">Ajoute flèches et labels</div>
                </div>
              </button>
            </div>
          )}
        </div>
        <button
          className={`pdf-mode-btn ${showDebug ? 'active' : ''}`}
          onClick={() => setShowDebug((v) => !v)}
          title="Afficher les logs de chargement IFC"
        >
          🐛 Debug
        </button>
      </div>

      {captureDraftUrl && captureDraftMeta && (
        <div className="ifc-capture-modal">
          <div className="ifc-capture-dialog">
            <div className="ifc-capture-head">
              <strong>
                {captureDraftKind === 'turntable'
                  ? 'Turntable 360°'
                  : captureDraftKind === 'annotated'
                  ? 'Annoter la capture'
                  : 'Capture 3D'}
              </strong>
              <button
                className="btn btn-ghost"
                style={{ padding: '2px 10px' }}
                onClick={() => {
                  setCaptureDraftUrl(null);
                  setCaptureDraftBlob(null);
                  setCaptureDraftMeta(null);
                  setAnnoItems([]);
                }}
              >
                ×
              </button>
            </div>
            <div className="ifc-capture-preview">
              {captureDraftKind === 'turntable' ? (
                <video src={captureDraftUrl} controls autoPlay loop style={{ maxWidth: '100%' }} />
              ) : (
                <div
                  style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}
                  onMouseDown={(e) => {
                    if (captureDraftKind !== 'annotated') return;
                    const target = e.currentTarget as HTMLDivElement;
                    const rect = target.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width;
                    const y = (e.clientY - rect.top) / rect.height;
                    const id = Math.random().toString(36).slice(2);
                    const label = prompt('Label de l\'annotation :', 'Élément') ?? '';
                    if (!label) return;
                    setAnnoItems((prev) => [
                      ...prev,
                      { id, x, y, tx: Math.min(1, x + 0.15), ty: Math.min(1, y + 0.12), label },
                    ]);
                  }}
                >
                  <img src={captureDraftUrl} alt="capture" style={{ maxWidth: '100%', display: 'block' }} />
                  {captureDraftKind === 'annotated' && (
                    <svg
                      className="ifc-anno-layer"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      {annoItems.map((a) => (
                        <g key={a.id}>
                          <line
                            x1={a.x * 100}
                            y1={a.y * 100}
                            x2={a.tx * 100}
                            y2={a.ty * 100}
                            stroke="#E07A5F"
                            strokeWidth="0.4"
                            vectorEffect="non-scaling-stroke"
                          />
                          <circle
                            cx={a.tx * 100}
                            cy={a.ty * 100}
                            r="1.2"
                            fill="#E07A5F"
                            onMouseDown={(ev) => {
                              ev.stopPropagation();
                              const wrap = (ev.currentTarget as SVGCircleElement).ownerSVGElement
                                ?.parentElement;
                              if (!wrap) return;
                              const rect = wrap.getBoundingClientRect();
                              const move = (m: MouseEvent) => {
                                const tx = Math.min(1, Math.max(0, (m.clientX - rect.left) / rect.width));
                                const ty = Math.min(1, Math.max(0, (m.clientY - rect.top) / rect.height));
                                setAnnoItems((prev) =>
                                  prev.map((it) => (it.id === a.id ? { ...it, tx, ty } : it))
                                );
                              };
                              const up = () => {
                                window.removeEventListener('mousemove', move);
                                window.removeEventListener('mouseup', up);
                              };
                              window.addEventListener('mousemove', move);
                              window.addEventListener('mouseup', up);
                            }}
                            style={{ cursor: 'grab' }}
                          />
                        </g>
                      ))}
                    </svg>
                  )}
                  {captureDraftKind === 'annotated' && (
                    <>
                      {annoItems.map((a) => (
                        <div
                          key={a.id + '_label'}
                          style={{
                            position: 'absolute',
                            left: `${a.x * 100}%`,
                            top: `${a.y * 100}%`,
                            transform: 'translate(8px, -100%)',
                            background: 'rgba(255,255,255,0.92)',
                            border: '1px solid #E07A5F',
                            color: '#3D5A45',
                            padding: '2px 6px',
                            fontSize: 12,
                            borderRadius: 4,
                            pointerEvents: 'none',
                          }}
                        >
                          {a.label}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              {captureDraftKind === 'annotated' && (
                <div className="card-meta" style={{ fontSize: 11, marginTop: 6 }}>
                  Clique sur l'image pour ajouter une flèche annotée, glisse le point pour déplacer.
                </div>
              )}
            </div>
            <div className="ifc-capture-form">
              <label className="card-meta" style={{ fontSize: 12 }}>
                Titre
              </label>
              <input
                type="text"
                value={captureTitle}
                onChange={(e) => setCaptureTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 6,
                  border: '1px solid var(--c-border)',
                  background: 'var(--c-bg)',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setCaptureDraftUrl(null);
                    setCaptureDraftBlob(null);
                    setCaptureDraftMeta(null);
                    setAnnoItems([]);
                  }}
                  disabled={captureSaving}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveCaptureDraft}
                  disabled={captureSaving || !captureTitle.trim()}
                >
                  {captureSaving ? 'Sauvegarde…' : 'Enregistrer dans la galerie'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {clipEnabled && clipBounds && (
        <div className="ifc-clip-panel">
          <div className="ifc-clip-head">
            <strong>Plans de coupe</strong>
            <span className="card-meta" style={{ fontSize: 11 }}>
              Active un axe puis glisse le slider
            </span>
          </div>
          {(['x', 'y', 'z'] as const).map((axis) => {
            const label = axis === 'x' ? 'Axe X (gauche ↔ droite)' : axis === 'y' ? 'Axe Y (haut ↔ bas)' : 'Axe Z (avant ↔ arrière)';
            const min = clipBounds.min[axis];
            const max = clipBounds.max[axis];
            const v = clipValues[axis];
            const on = clipAxes[axis];
            return (
              <div key={axis} className={`ifc-clip-row ${on ? 'on' : ''}`}>
                <label className="ifc-clip-axis">
                  <input type="checkbox" checked={on} onChange={() => toggleAxis(axis)} />
                  {label}
                </label>
                <input
                  type="range"
                  disabled={!on}
                  min={min}
                  max={max}
                  step={(max - min) / 200}
                  value={v}
                  onChange={(e) => moveClip(axis, Number(e.target.value))}
                />
                <span className="card-meta" style={{ fontSize: 11, minWidth: 64 }}>
                  {axis.toUpperCase()} = {v.toFixed(2)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {showDebug && (
        <div className="ifc-debug">
          <div className="ifc-debug-head">
            <strong>Log IFC</strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn btn-ghost"
                style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => {
                  navigator.clipboard?.writeText(debugLines.join('\n'));
                }}
              >
                Copier
              </button>
              <button
                className="btn btn-ghost"
                style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => setShowDebug(false)}
              >
                ×
              </button>
            </div>
          </div>
          <div className="ifc-debug-body">
            {debugLines.map((l, i) => (
              <div key={i} style={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>
                {l}
              </div>
            ))}
          </div>
        </div>
      )}

      {sideOpen && (
        <aside className="ifc-side">
          <div className="ifc-side-tabs">
            <button
              className={tab === 'props' ? 'side-tab active' : 'side-tab'}
              onClick={() => setTab('props')}
            >
              Propriétés
            </button>
            <button
              className={tab === 'annotations' ? 'side-tab active' : 'side-tab'}
              onClick={() => setTab('annotations')}
            >
              Annotations
            </button>
            <button
              className={tab === 'categories' ? 'side-tab active' : 'side-tab'}
              onClick={() => setTab('categories')}
            >
              Catégories
            </button>
          </div>

          {tab === 'props' && (
            <div className="ifc-panel-body">
              {!selected ? (
                <div className="card-meta" style={{ padding: 12 }}>
                  Clique sur un élément du modèle pour voir ses propriétés IFC.
                </div>
              ) : (
                <div className="card">
                  {/* En-tête contextuel bien lisible */}
                  <div className="ifc-props-header">
                    <span className="ifc-type-chip">{selected.ifcType}</span>
                    <div className="ifc-name">{selected.name ?? '(sans nom)'}</div>
                    {selected.predefinedType && (
                      <div className="ifc-predef">
                        {labelForEntity(selected.ifcType)} · {selected.predefinedType}
                      </div>
                    )}

                    <div className="ifc-info-grid">
                      {selected.storey && (
                        <>
                          <span className="ifc-info-label">Étage</span>
                          <span className="ifc-info-value">
                            {selected.storey.name ?? `#${selected.storey.expressID}`}
                          </span>
                        </>
                      )}
                      {selected.space && (
                        <>
                          <span className="ifc-info-label">Espace</span>
                          <span className="ifc-info-value">
                            {selected.space.name ?? `#${selected.space.expressID}`}
                          </span>
                        </>
                      )}
                      {selected.materials.length > 0 && (
                        <>
                          <span className="ifc-info-label">Matériau</span>
                          <span className="ifc-info-value">
                            {selected.materials.join(' · ')}
                          </span>
                        </>
                      )}
                      <span className="ifc-info-label">GlobalId</span>
                      <span
                        className="ifc-info-value"
                        style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}
                      >
                        {selected.globalId ?? `expressID ${selected.expressID}`}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, marginBottom: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    {canEdit && selected.globalId && documentId && (
                      <>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '6px 10px', fontSize: 12 }}
                          onClick={() => setCommentOpen(true)}
                        >
                          💬 Commenter
                        </button>
                        {!recording ? (
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '6px 10px', fontSize: 12 }}
                            onClick={startRecording}
                          >
                            🎤 Mémo vocal
                          </button>
                        ) : (
                          <button
                            className="btn btn-revoir"
                            style={{ padding: '6px 10px', fontSize: 12 }}
                            onClick={stopRecording}
                          >
                            ⏹ Arrêter
                          </button>
                        )}
                      </>
                    )}
                    <button
                      className="btn btn-ghost"
                      style={{ padding: '6px 10px', fontSize: 12 }}
                      onClick={hideSelected}
                      title="Masquer cet élément du modèle (récupérable dans l'onglet Catégories)"
                    >
                      👁‍🗨 Masquer
                    </button>
                    <button
                      className={`btn ${isolating ? 'btn-revoir' : 'btn-ghost'}`}
                      style={{ padding: '6px 10px', fontSize: 12 }}
                      onClick={() =>
                        isolating
                          ? stateRef.current?.unisolate?.()
                          : stateRef.current?.isolateSelected?.()
                      }
                      title={
                        isolating
                          ? 'Réafficher tous les éléments'
                          : 'Masquer tout sauf cet élément'
                      }
                    >
                      {isolating ? '◎ Annuler isolation' : '🎯 Isoler'}
                    </button>
                    <button
                      className={`btn ${ghostMode ? 'btn-valider' : 'btn-ghost'}`}
                      style={{ padding: '6px 10px', fontSize: 12 }}
                      onClick={() => {
                        const next = !ghostMode;
                        stateRef.current?.applyGhost?.(next);
                        setGhostMode(next);
                      }}
                      title="Rend les autres éléments semi-transparents pour faire ressortir la sélection"
                    >
                      {ghostMode ? '👁 Ghost ON' : '👻 Ghost'}
                    </button>
                  </div>

                  {commentOpen && (
                    <div style={{ marginBottom: 10 }}>
                      <textarea
                        autoFocus
                        rows={3}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Commentaire ancré à cet élément…"
                        style={{
                          width: '100%',
                          padding: 8,
                          border: '1px solid var(--c-border)',
                          borderRadius: 6,
                          background: 'var(--c-bg)',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => {
                            setCommentOpen(false);
                            setCommentText('');
                          }}
                        >
                          Annuler
                        </button>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={addTextAnnotation}
                        >
                          Enregistrer
                        </button>
                      </div>
                    </div>
                  )}

                  {annsForSelected.length > 0 && (
                    <>
                      <div className="card-meta" style={{ marginBottom: 4, marginTop: 8 }}>
                        Annotations sur cet élément ({annsForSelected.length})
                      </div>
                      {annsForSelected.map((a) => (
                        <div
                          key={a.id}
                          style={{
                            padding: 8,
                            background: 'var(--c-nav)',
                            borderRadius: 6,
                            fontSize: 13,
                            marginBottom: 6,
                          }}
                        >
                          <div
                            className="card-meta"
                            style={{
                              fontSize: 11,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span>
                              {a.user_name} · {new Date(a.created_at).toLocaleString('fr-FR')}
                            </span>
                            <button
                              onClick={() => deleteAnnotation(a.id)}
                              title="Supprimer"
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#8a2f1c',
                                cursor: 'pointer',
                                fontSize: 14,
                                padding: 2,
                              }}
                            >
                              🗑
                            </button>
                          </div>
                          {a.type === 'vocal' && a.voice_key ? (
                            <audio
                              controls
                              src={`/api/v1/files?key=${encodeURIComponent(a.voice_key)}`}
                              style={{ width: '100%', marginTop: 4 }}
                            />
                          ) : (
                            <div style={{ marginTop: 2 }}>{a.content}</div>
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {selected.psets.length > 0 && (
                    <>
                      <div
                        className="card-meta"
                        style={{
                          marginBottom: 4,
                          marginTop: 10,
                          textTransform: 'uppercase',
                          fontSize: 10,
                        }}
                      >
                        PropertySets ({selected.psets.length})
                      </div>
                      {selected.psets.map((ps, i) => (
                        <PsetView key={i} pset={ps} />
                      ))}
                    </>
                  )}

                  {selected.psets.length === 0 && (
                    <div
                      className="card-meta"
                      style={{ fontSize: 11, marginTop: 10, fontStyle: 'italic' }}
                    >
                      Aucun PropertySet associé à cet élément.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === 'annotations' && (
            <div className="ifc-panel-body">
              <div className="card">
                <div className="card-meta" style={{ marginBottom: 8 }}>
                  Toutes les annotations ancrées à un élément 3D ({allIfcAnnotations.length}).
                </div>
                {allIfcAnnotations.length === 0 ? (
                  <div className="card-meta">Aucune.</div>
                ) : (
                  allIfcAnnotations.map((a) => {
                    const info = annotationInfos[a.id];
                    const hasElement = !!info;
                    return (
                      <div key={a.id} className="ifc-ann-card">
                        <div
                          className="ifc-ann-head"
                          onClick={() => onAnnotationListClick(a.ifc_global_id)}
                          title={hasElement ? 'Cliquer pour zoomer sur l\'élément' : 'Élément introuvable dans le modèle'}
                          style={{ cursor: hasElement ? 'pointer' : 'default' }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {info ? (
                              <>
                                <div style={{ fontWeight: 600, fontSize: 13 }}>
                                  {info.name ?? '(sans nom)'}
                                </div>
                                <div className="card-meta" style={{ fontSize: 11 }}>
                                  {info.ifcType}
                                  {info.storey ? ` · ${info.storey.name ?? 'étage'}` : ''}
                                  {info.materials.length > 0 ? ` · ${info.materials[0]}` : ''}
                                </div>
                              </>
                            ) : (
                              <div className="card-meta" style={{ fontSize: 12 }}>
                                Élément introuvable (GlobalId non mappé)
                              </div>
                            )}
                          </div>
                          {hasElement && (
                            <span className="card-meta" style={{ fontSize: 14, marginLeft: 8 }}>
                              🔍
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAnnotation(a.id);
                            }}
                            title="Supprimer cette annotation"
                            className="ifc-ann-del"
                          >
                            🗑
                          </button>
                        </div>

                        <div className="card-meta" style={{ fontSize: 11, marginTop: 6 }}>
                          {a.user_name} · {new Date(a.created_at).toLocaleString('fr-FR')}
                        </div>
                        {a.type === 'vocal' && a.voice_key ? (
                          <audio
                            controls
                            src={`/api/v1/files?key=${encodeURIComponent(a.voice_key)}`}
                            style={{ width: '100%', marginTop: 4 }}
                          />
                        ) : (
                          <div style={{ marginTop: 2, fontSize: 13 }}>{a.content}</div>
                        )}
                        <div
                          className="card-meta"
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 10,
                            marginTop: 4,
                            wordBreak: 'break-all',
                          }}
                        >
                          {a.ifc_global_id}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {tab === 'categories' && (
            <div className="ifc-panel-body">
              {hiddenElements.length > 0 && (
                <div className="card" style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 6,
                    }}
                  >
                    <div className="card-title" style={{ fontSize: 13 }}>
                      Éléments masqués ({hiddenElements.length})
                    </div>
                    <button
                      className="btn btn-valider"
                      style={{ padding: '4px 10px', fontSize: 11 }}
                      onClick={showAllHidden}
                    >
                      Tout réafficher
                    </button>
                  </div>
                  {hiddenElements.map((h) => (
                    <div
                      key={h.expressID}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderTop: '1px solid var(--c-border)',
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: 12 }}>{h.name}</div>
                        <div className="card-meta" style={{ fontSize: 10 }}>
                          {h.ifcType}
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={() => showHiddenElement(h)}
                      >
                        Réafficher
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="card">
                <div className="card-meta" style={{ marginBottom: 8 }}>
                  {entitiesInModel.length} type{entitiesInModel.length > 1 ? 's' : ''} IFC détecté
                  {entitiesInModel.length > 1 ? 's' : ''} dans ce modèle. Clique pour déplier et
                  naviguer élément par élément.
                </div>
                {entitiesInModel.length === 0 && (
                  <div className="card-meta">Aucun élément IFC détecté.</div>
                )}
                {entitiesInModel.map(({ entity, count }) => {
                  const isHidden = hiddenEntities.has(entity);
                  const isExpanded = expandedEntity === entity;
                  const ids = stateRef.current?.byEntity?.get?.(entity) ?? [];
                  return (
                    <div
                      key={entity}
                      style={{ borderTop: '1px solid var(--c-border)', padding: '6px 0' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                        }}
                        onClick={() => setExpandedEntity(isExpanded ? null : entity)}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>
                            {isExpanded ? '▾' : '▸'} {labelForEntity(entity)}
                          </div>
                          <div
                            className="card-meta"
                            style={{ fontSize: 10, fontFamily: 'monospace' }}
                          >
                            {entity} — {count} élément{count > 1 ? 's' : ''}
                          </div>
                        </div>
                        <button
                          className={`btn ${isHidden ? 'btn-ghost' : 'btn-valider'}`}
                          style={{ padding: '4px 10px', fontSize: 11, minWidth: 80 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEntity(entity);
                          }}
                        >
                          {isHidden ? 'Masqué' : 'Visible'}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="ifc-entity-items">
                          {ids.length === 0 && (
                            <div className="card-meta" style={{ padding: 6, fontSize: 11 }}>
                              Aucun élément.
                            </div>
                          )}
                          {ids.map((id: number) => {
                            const props = stateRef.current?.localProps?.[id];
                            const n = unwrap(props?.Name) as string | null;
                            const label = n ?? `#${id}`;
                            return (
                              <div
                                key={id}
                                className="ifc-entity-item"
                                onClick={() => stateRef.current?.selectByExpressID?.(id)}
                                title="Cliquer pour zoomer et sélectionner"
                              >
                                <span>📄</span>
                                <span className="ifc-entity-item-label">{label}</span>
                                <span className="card-meta" style={{ fontSize: 10 }}>
                                  #{id}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </aside>
      )}

      {recording && (
        <div className="recording-bar">
          <span className="rec-dot" /> Enregistrement sur l'élément sélectionné…
          <button className="btn btn-primary" onClick={stopRecording} style={{ marginLeft: 12 }}>
            Arrêter
          </button>
        </div>
      )}
    </div>
  );
}

function PsetView({ pset }: { pset: PsetEntry }) {
  const entries = Object.entries(pset.props);
  return (
    <div
      style={{
        border: '1px solid var(--c-border)',
        borderRadius: 6,
        padding: 8,
        marginBottom: 6,
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{pset.name}</div>
      {entries.length === 0 && <div className="card-meta" style={{ fontSize: 11 }}>(vide)</div>}
      {entries.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 6,
            padding: '3px 0',
            fontSize: 12,
            borderTop: '1px solid var(--c-border)',
          }}
        >
          <div className="card-meta" style={{ fontSize: 11 }}>{k}</div>
          <div style={{ wordBreak: 'break-word' }}>{formatPropValue(v)}</div>
        </div>
      ))}
    </div>
  );
}

function formatPropValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.map((x) => formatPropValue(x)).join(', ');
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    if ('value' in obj) return String(obj.value);
    return JSON.stringify(obj);
  }
  return String(v);
}

/**
 * Construit ElementInfo à partir d'un expressID et de la map globale localProps.
 * Les relations Psets, étage, matériaux sont trouvées en scannant localProps
 * (les IfcRelDefinesByProperties, IfcRelContainedInSpatialStructure, IfcRelAssociatesMaterial
 * sont eux aussi dans cette map).
 */
function buildElementInfo(
  expressID: number,
  props: any,
  allProps: Record<number, any>,
  typeNameMap: Map<number, string>,
  WEBIFC: any | null
): ElementInfo {
  if (!props) {
    return {
      expressID,
      ifcType: 'UNKNOWN',
      name: null,
      globalId: null,
      predefinedType: null,
      psets: [],
      storey: null,
      space: null,
      materials: [],
    };
  }

  const typeCode = typeof props.type === 'number' ? props.type : null;
  const ifcType = typeCode !== null ? typeNameMap.get(typeCode) ?? `TYPE_${typeCode}` : 'UNKNOWN';
  const name = (unwrap(props.Name) as string | null) ?? null;
  const globalId = (unwrap(props.GlobalId) as string | null) ?? null;
  const predefinedType = (unwrap(props.PredefinedType) as string | null) ?? null;

  const IFCRELDEFINESBYPROPERTIES = (WEBIFC as any)?.IFCRELDEFINESBYPROPERTIES;
  const IFCRELCONTAINEDINSPATIALSTRUCTURE = (WEBIFC as any)?.IFCRELCONTAINEDINSPATIALSTRUCTURE;
  const IFCRELASSOCIATESMATERIAL = (WEBIFC as any)?.IFCRELASSOCIATESMATERIAL;

  const hasId = (list: any[], id: number): boolean =>
    Array.isArray(list) && list.some((x) => unwrap(x) === id);

  // Psets
  const psets: PsetEntry[] = [];
  try {
    for (const [, p] of Object.entries(allProps)) {
      if ((p as any)?.type !== IFCRELDEFINESBYPROPERTIES) continue;
      const rel = p as any;
      if (!hasId(rel.RelatedObjects, expressID)) continue;
      const psetID = unwrap(rel.RelatingPropertyDefinition);
      if (typeof psetID !== 'number') continue;
      const pset = allProps[psetID];
      if (!pset) continue;
      const psetName = (unwrap(pset.Name) as string | null) ?? 'Pset';
      const propsMap: Record<string, unknown> = {};
      const handles = pset.HasProperties ?? [];
      for (const h of handles) {
        const pID = unwrap(h);
        if (typeof pID !== 'number') continue;
        const prop = allProps[pID];
        if (!prop) continue;
        const pname = unwrap(prop.Name) as string | null;
        if (!pname) continue;
        let pvalue: unknown = unwrap(prop.NominalValue);
        if (pvalue === undefined || pvalue === null) {
          pvalue = prop.EnumerationValues ?? unwrap(prop.Value) ?? null;
        }
        propsMap[pname] = pvalue;
      }
      psets.push({ name: psetName, props: propsMap });
    }
  } catch {
    /* noop */
  }

  // Étage (IfcBuildingStorey) + Espace (IfcSpace) via ContainedInStructure et ReferencedInSpatialStructure
  let storey: ElementInfo['storey'] = null;
  let space: ElementInfo['space'] = null;
  const IFCBUILDINGSTOREY_CODE = (WEBIFC as any)?.IFCBUILDINGSTOREY;
  const IFCSPACE_CODE = (WEBIFC as any)?.IFCSPACE;
  const IFCRELREFERENCEDINSPATIALSTRUCTURE_CODE = (WEBIFC as any)?.IFCRELREFERENCEDINSPATIALSTRUCTURE;
  try {
    for (const [, p] of Object.entries(allProps)) {
      const pp = p as any;
      const isContained = pp?.type === IFCRELCONTAINEDINSPATIALSTRUCTURE;
      const isReferenced =
        IFCRELREFERENCEDINSPATIALSTRUCTURE_CODE !== undefined &&
        pp?.type === IFCRELREFERENCEDINSPATIALSTRUCTURE_CODE;
      if (!isContained && !isReferenced) continue;
      if (!hasId(pp.RelatedElements, expressID)) continue;
      const containerID = unwrap(pp.RelatingStructure);
      if (typeof containerID !== 'number') continue;
      const container = allProps[containerID];
      if (!container) continue;
      const containerType = container.type;
      const containerName = (unwrap(container.Name) as string | null) ?? null;
      if (containerType === IFCSPACE_CODE && !space) {
        space = { expressID: containerID, name: containerName };
      } else if (containerType === IFCBUILDINGSTOREY_CODE && !storey) {
        storey = { expressID: containerID, name: containerName };
      }
      if (storey && space) break;
    }
    // Si on a un IfcSpace mais pas d'étage, remonter via la hiérarchie IsDecomposedBy de l'espace
    if (space && !storey) {
      // l'espace est décomposé depuis un étage : IfcRelAggregates avec RelatedObjects = [spaceID]
      const IFCRELAGGREGATES = (WEBIFC as any)?.IFCRELAGGREGATES;
      for (const [, p] of Object.entries(allProps)) {
        const pp = p as any;
        if (pp?.type !== IFCRELAGGREGATES) continue;
        if (!hasId(pp.RelatedObjects, space.expressID)) continue;
        const parentID = unwrap(pp.RelatingObject);
        if (typeof parentID !== 'number') continue;
        const parent = allProps[parentID];
        if (parent?.type === IFCBUILDINGSTOREY_CODE) {
          storey = { expressID: parentID, name: (unwrap(parent.Name) as string | null) ?? null };
          break;
        }
      }
    }
  } catch {
    /* noop */
  }

  // Matériaux
  const materials: string[] = [];
  try {
    for (const [, p] of Object.entries(allProps)) {
      if ((p as any)?.type !== IFCRELASSOCIATESMATERIAL) continue;
      const rel = p as any;
      if (!hasId(rel.RelatedObjects, expressID)) continue;
      const matID = unwrap(rel.RelatingMaterial);
      if (typeof matID !== 'number') continue;
      const mat = allProps[matID];
      if (!mat) continue;
      const direct = unwrap(mat.Name) as string | null;
      if (direct) {
        materials.push(direct);
      } else if (mat.ForLayerSet) {
        const lsID = unwrap(mat.ForLayerSet);
        const ls = typeof lsID === 'number' ? allProps[lsID] : null;
        const layers = ls?.MaterialLayers ?? [];
        for (const lh of layers) {
          const layerID = unwrap(lh);
          const layer = typeof layerID === 'number' ? allProps[layerID] : null;
          const layerMatID = unwrap(layer?.Material);
          const layerMat = typeof layerMatID === 'number' ? allProps[layerMatID] : null;
          const n = unwrap(layerMat?.Name) as string | null;
          if (n) materials.push(n);
        }
      }
    }
  } catch {
    /* noop */
  }

  return {
    expressID,
    ifcType,
    name,
    globalId,
    predefinedType,
    psets,
    storey,
    space,
    materials: Array.from(new Set(materials)),
  };
}
